"use client";

import { useState, useEffect } from "react";
import { Trophy, Medal, Award, ThumbsUp, Users, Loader } from "lucide-react";
import TypeaheadInput from "./TypeaheadInput";
import { useIsMobile } from "../../hooks/use-mobile";
import {
  getPollStatitics,
  getSuggestions,
  submitPollResponse,
} from "../../services/poll";
import { successToast,errorToast } from '../../utils/toasters'
import { isAuthenticated } from "../../utils/helpers";

function QuestionCard({ question }) {
  const [answer, setAnswer] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pollStatitics, setPollStatitics] = useState(null);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  const isMobile = useIsMobile();

  useEffect(() => {
    async function fetchInitialPollStatistics() {
      try {
        setIsSubmitting(true);
        const pollStatitics = await getPollStatitics(question.question_id);
        setPollStatitics(pollStatitics.data.top_answers);
        setTotalSubmissions(pollStatitics.data.total_submissions);
        setSubmitted(true);
      } catch (error) {
        console.error("fetching initial poll failed error: ", error);
      }
    }
    if (question.isSubmitted) {
      fetchInitialPollStatistics();
    }
  }, [question]);

  useEffect(() => {
    async function fetchSuggestions() {
      try {
        const question_id = question.question_id;
        const suggestionsResponse = await getSuggestions(question_id, answer);
        setSuggestions(suggestionsResponse.data.suggestions);
      } catch (error) {
        console.error("Error while fetching questions : ", error);
      }
    }
    if (!question.is_multiple_choice && answer.trim() != "") {
      fetchSuggestions();
    }
  }, [answer]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const geohash = localStorage.getItem("geohash") || "9v6m";
      if (!question.is_multiple_choice && answer.trim() === "") {
        throw new Error("Answer cannot be Empty.");
      }
      if (!isAuthenticated()) {
        throw new Error("Please Login Before Submitting Your Response");
      }

      const payload = {
        question_id: question.question_id,
        geohash: geohash,
      };
      if (!question.is_multiple_choice) {
        payload.answer_text = answer;
      } else {
        payload.selected_choice_id = answer;
      }
      const pollSubmissionResponse = await submitPollResponse(payload);
      successToast("Your Response has been successfully submitted.");

      const pollStatitics = await getPollStatitics(question.question_id);

      setPollStatitics(pollStatitics.data.top_answers);
      setTotalSubmissions(pollStatitics.data.total_submissions);
      setSubmitted(true);
    } catch (error) {
      errorToast(error.message ?? "Error while submitting answer.");
      console.error("Error while fetching questions : ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get rank icons for top positions
  const getRankIcon = (index) => {
    switch (index) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Award className="h-5 w-5 text-amber-700" />;
      default:
        return <ThumbsUp className="h-5 w-5 text-[#7B189F]/60" />;
    }
  };

  return (
    <div
      className={`${
        isMobile ? "w-[350px]" : "w-full max-w-md"
      } border rounded-lg overflow-visible shadow-sm transition-all duration-300 hover:shadow-md bg-white`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#7B189F]/10 to-[#7B189F]/5 border-b border-[#7B189F]/10 rounded-t-lg p-6">
        <h2
          className={`text-[#7B189F] ${
            isMobile ? "text-lg" : "text-xl"
          } font-bold ${isMobile ? "leading-tight" : ""}`}
        >
          {question.question_text}
        </h2>
        <p className="text-[#7B189F]/70 text-sm mt-1">
          {!submitted ? "Share your thoughts with us" : "Results"}
        </p>
      </div>

      {/* Content */}
      <div className="pt-6 pb-4 px-6 relative">
        {!submitted ? (
          <div className="animate-fade-in">
            {!question.is_multiple_choice ? (
              <div className="space-y-4">
                <TypeaheadInput
                  value={answer}
                  onChange={setAnswer}
                  suggestions={suggestions || []}
                  placeholder="Type your answer here..."
                />
              </div>
            ) : (
              <div className="space-y-3">
                {question.choices.map((option) => (
                  <div
                    key={option.choice_id}
                    className={`flex items-center space-x-2 rounded-md border border-[#7B189F]/20 p-4 transition-all duration-200 hover:bg-[#7B189F]/5 hover:border-[#7B189F]/40 cursor-pointer ${
                      answer === option.choice_id ? "bg-[#7B189F]/10" : ""
                    }`}
                    onClick={() => setAnswer(option.choice_id)}
                  >
                    <div className="flex items-center justify-center h-5 w-5">
                      <div
                        className={`h-3 w-3 rounded-full ${
                          answer === option.choice_id
                            ? "bg-[#7B189F]"
                            : "border border-[#7B189F]/50"
                        }`}
                      />
                    </div>
                    <label className="flex-1 cursor-pointer font-medium text-gray-800">
                      {option.choice_text}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="animate-fade-in py-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#7B189F]">
                Poll Results
              </h3>
              <div className="flex items-center text-sm text-gray-500">
                <Users className="h-4 w-4 mr-1" />
                <span>{totalSubmissions && totalSubmissions} votes</span>
              </div>
            </div>

            <div
              className={`h-${
                isMobile ? "[200px]" : "[280px]"
              } overflow-y-auto pr-2 space-y-4 custom-scrollbar`}
            >
              {pollStatitics &&
                pollStatitics.map((result, index) => {
                  const percentage =
                    Math.round((result.count / totalSubmissions) * 100) || 0;

                  return (
                    <div
                      key={index}
                      className="relative mb-3 result-item"
                      style={{
                        animationDelay: `${index * 0.1}s`,
                      }}
                    >
                      <div className="flex items-center mb-1.5">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7B189F]/10 mr-3 flex-shrink-0">
                          {getRankIcon(index)}
                        </div>
                        <div className="font-medium truncate">
                          {result.text}
                        </div>
                        <div className="ml-auto font-semibold text-[#7B189F] pl-2 flex-shrink-0">
                          {percentage}%
                        </div>
                      </div>

                      <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#7B189F] rounded-full progress-bar"
                          style={{
                            maxWidth: `${percentage}%`,
                            animationDelay: `${index * 0.1 + 0.3}s`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="mt-6 p-3 bg-[#7B189F]/5 rounded-lg border border-[#7B189F]/10">
              <p className="text-sm text-center text-[#7B189F]/80">
                Thank you for your response! Your answer has been recorded.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex justify-end border-t border-[#7B189F]/10 bg-gradient-to-r from-[#7B189F]/5 to-[#7B189F]/10 p-4">
        {!submitted ? (
          <button
            onClick={handleSubmit}
            disabled={!answer || isSubmitting}
            className="bg-[#7B189F] hover:bg-[#7B189F]/90 text-white py-2 px-6 rounded-md transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:shadow-none font-medium"
          >
            {isSubmitting ? (
              <Loader classname="animate-spin" />
            ) : (
              "Submit Answer"
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default QuestionCard;
